<script lang="ts">
    import Vue from "vue";
    import Component from 'vue-class-component';
    import { AttributeReadModel } from '@Alternatives/Attributes/Domain/readmodels';
    import { FormReadModel } from '@Alternatives/Forms/Domain/readmodels';
    import { ViewSettingsEditorViewModel } from '@Alternatives/AlternativesLists/Components/ViewSettingsEditor';

    @Component({
        props: {
            viewModel: {
                type: Object,
                required: true
            }
        }
    })
    export default class ViewSettingsEditor extends Vue
    {
        get vm(): ViewSettingsEditorViewModel { return this.$props.viewModel as ViewSettingsEditorViewModel; }
        get opened(): boolean { return this.vm?.opened ?? false; }

        get alternativesLabel(): string { return this.vm.alternativesLabel; }
        get forms(): FormReadModel[] { return this.vm.forms; }
        get attributes(): AttributeReadModel[] { return this.vm.attributes; }
        get listAttributes() { return this.attributes?.filter(a => a.IsList === true); }

        get formId(): number | null { return this.vm.formId; }
        set formId(val: number | null) { this.vm.viewSettings.FormId = val; }

        get colorAttributeId(): number | null { return this.vm.colorAttributeId; }
        set colorAttributeId(val: number | null) { this.vm.viewSettings.ColorAttributeId = val; }

        get columnAttributeId(): number | null { return this.vm.columnAttributeId; }
        set columnAttributeId(val: number | null) { this.vm.viewSettings.ColumnAttributeId = val; }

        confirm()
        {
            this.vm.save();
            this.$emit('saved');
            this.close();
        }

        close()
        {
            this.vm.close();
        }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Configure View</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="form-group">
                            <label>Form:</label>
                            <select class="form-control" v-model="formId">
                                <option :value="null">All fields (default)</option>
                                <option v-for="f in forms" :value="f.Id">{{f.Name}}</option>
                            </select>
                            <small class="form-text text-muted">
                                You can decide which form you want to open when you edit <em>{{alternativesLabel}}</em>. 
                                Simply select the form on the drop-down. If you don't like any of the existing forms, why not create one of your own!
                            </small>
                        </div>

                        <div class="form-group">
                            <label>Color:</label>
                            <select class="form-control" v-model="colorAttributeId">
                                <option :value="null">None (default)</option>
                                <option v-for="a in listAttributes" :value="a.Id">{{a.Name}}</option>
                            </select>                            
                            <small class="form-text text-muted">
                                You can color-code your <em>{{alternativesLabel}}</em> based on any category-based attribute.
                                This will help you quickly eyeball your <em>{{alternativesLabel}}</em> and and get a feel for how many fall into each category.
                                Simply select which attribute you want to use to color-code your <em>{{alternativesLabel}}</em> within the Kanban.
                            </small>
                        </div>

                        <div class="form-group">
                            <label>Columns:</label>
                            <select class="form-control" v-model="columnAttributeId">
                                <option :value="null">Status (default)</option>
                                <option v-for="a in listAttributes" :value="a.Id">{{a.Name}}</option>
                            </select>
                            <small class="form-text text-muted">
                                By default, your Kanban will break <em>{{alternativesLabel}}</em> out by status.
                                This lets you set out the "workflow" of <em>{{alternativesLabel}}</em>, but sometimes you might want to see <em>{{alternativesLabel}}</em> broken out by other factors.
                                You can select any category-attribute simply by selecting that attribute from this menu... and when you're done, you can come back and change it back to status!
                            </small>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button @click="confirm" type="button" class="btn btn-primary">Confirm</button>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>      